import { supabase } from '@/lib/supabase';

export enum BADGE_TYPES {
  COMPLETION = 'completion',
  ACHIEVEMENT = 'achievement',
  PARTICIPATION = 'participation',
  EXCELLENCE = 'excellence'
}

export interface Badge {
  id?: number; // Optional ID if stored in the DB
  name: string;
  description: string;
  type: BADGE_TYPES;
  criteria?: string;
  image?: string;
  learning_path_id?: string;
  slug?: string;
}

/**
 * Fetch course information and return a badge object.
 * If a user ID is provided, the function uses learnApi to get
 * the user's course progress details; otherwise, it falls back to a
 * direct query on the courses table.
 */
export const getBadgeForCourse = async (
  courseId: string,
  userId?: string
): Promise<Badge | null> => {
  try {
    // First get all courses
    const { data: courses, error: coursesError } = await supabase
      .from('courses')
      .select('*');

    if (coursesError || !courses) {
      console.error('Error fetching courses:', coursesError);
      return null;
    }

    // Find the specific course
    const courseData = courses.find(course => course.id === Number(courseId));

    if (!courseData) {
      console.error('Course not found:', courseId);
      return null;
    }

    // Then get user progress if userId is provided
    let progressData;
    if (userId) {
      const { data, error } = await supabase
        .from('user_course_progress')
        .select('*')
        .eq('course_id', courseId)
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching user_course_progress:', error);
        return null;
      }
      progressData = data;
    }

    return {
      name: courseData.title,
      description: courseData.description,
      type: BADGE_TYPES.COMPLETION,
      criteria: `Complete o curso ${courseData.title}`,
      // Additional metadata if needed
      learning_path_id: courseData.learning_path_id,
      slug: courseData.slug
    };
  } catch (error) {
    console.error('Error getting badge for course:', error);
    return null;
  }
};

/**
 * Creates or updates a badge class.
 * This function assumes you have a "badge_classes" table set up in Supabase.
 */
export const createBadgeClass = async (badge: Badge): Promise<Badge> => {
  try {
    const { data, error } = await supabase
      .from('badge_classes')
      .upsert({
        name: badge.name,
        description: badge.description,
        type: badge.type,
        criteria: badge.criteria,
        image: badge.image
      })
      .select()
      .maybeSingle();
    
    if (error || !data) {
      console.error('Error creating/updating badge class:', error);
      throw error;
    }
    return data;
  } catch (error) {
    console.error('Error in createBadgeClass:', error);
    throw error;
  }
};

/**
 * Creates a badge assertion for a user.
 * This function inserts a record into the "badge_assertions" table.
 */
export const createBadgeAssertion = async (userId: string, badgeId: string) => {
  try {
    const { data, error } = await supabase
      .from('badge_assertions')
      .insert({
        user_id: userId,
        badge_id: badgeId,
        issued_on: new Date().toISOString()
      })
      .select()
      .maybeSingle();
    
    if (error || !data) {
      console.error('Error creating badge assertion:', error);
      throw error;
    }
    return data;
  } catch (error) {
    console.error('Error in createBadgeAssertion:', error);
    throw error;
  }
};

/**
 * Helper to convert an SVG string into a PNG data URL.
 */
const svgToPng = async (svgString: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const svg = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svg);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 800;  // 2x size for better quality
      canvas.height = 800;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Set white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the SVG image onto the canvas
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png', 1.0));
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error loading SVG'));
    };

    img.src = url;
  });
};


/**
 * Generates a dynamic SVG badge image with animations and styling
 * @param name - Course or achievement name
 * @param description - Badge description
 * @param type - Badge type (COMPLETION, EXCELLENCE, or IN_PROGRESS)
 * @returns Promise containing base64 encoded SVG data URL
 */
export const generateBadgeImage = async (
  name: string | undefined,
  description: string,
  type: BADGE_TYPES
): Promise<string> => {
  try {
    const svgTemplate = `
      <svg width="400" height="400" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <!-- Gradients -->
          <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#2D3436;stop-opacity:1">
              <animate attributeName="stop-color" 
                values="#2D3436;#434C5E;#2D3436" 
                dur="4s" 
                repeatCount="indefinite"/>
            </stop>
            <stop offset="100%" style="stop-color:#000B18;stop-opacity:1">
              <animate attributeName="stop-color" 
                values="#000B18;#1E2A3B;#000B18" 
                dur="4s" 
                repeatCount="indefinite"/>
            </stop>
          </linearGradient>

          <linearGradient id="accentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#00F260;stop-opacity:1"/>
            <stop offset="100%" style="stop-color:#0575E6;stop-opacity:1"/>
          </linearGradient>

          <!-- Patterns -->
          <pattern id="codePattern" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
            <text x="0" y="10" fill="#ffffff" opacity="0.07" font-family="monospace" font-size="10">
              {code}
            </text>
            <text x="0" y="25" fill="#ffffff" opacity="0.07" font-family="monospace" font-size="10">
              function()
            </text>
            <text x="0" y="40" fill="#ffffff" opacity="0.07" font-family="monospace" font-size="10">
              const dev =
            </text>
          </pattern>

          <!-- Filters -->
          <filter id="neonGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        <!-- Background with Pattern -->
        <path d="M200 30
                 L340 115
                 L340 285
                 L200 370
                 L60 285
                 L60 115 Z" 
              fill="url(#bgGradient)"/>
        <path d="M200 30
                 L340 115
                 L340 285
                 L200 370
                 L60 285
                 L60 115 Z" 
              fill="url(#codePattern)"
              opacity="0.8"/>

        <!-- Hexagonal Frame -->
        <path d="M200 30
                 L340 115
                 L340 285
                 L200 370
                 L60 285
                 L60 115 Z" 
              fill="none" 
              stroke="url(#accentGradient)" 
              stroke-width="2"
              opacity="0.8"
              filter="url(#neonGlow)"/>

        <!-- Inner Hexagon -->
        <path d="M200 60
                 L315 130
                 L315 270
                 L200 340
                 L85 270
                 L85 130 Z" 
              fill="#1A1E23" 
              opacity="0.9"/>

        <!-- Animated Tech Circles -->
        <g transform="translate(200, 200)">
          <circle r="140" 
                  fill="none" 
                  stroke="url(#accentGradient)" 
                  stroke-width="0.5" 
                  stroke-dasharray="4,4"
                  opacity="0.3">
            <animateTransform attributeName="transform"
                              type="rotate"
                              from="0"
                              to="360"
                              dur="30s"
                              repeatCount="indefinite"/>
          </circle>
        </g>

        <!-- Badge Content -->
        <g transform="translate(200,200)" text-anchor="middle">
          <text y="-40" 
                font-family="monospace" 
                font-size="24" 
                fill="url(#accentGradient)" 
                filter="url(#neonGlow)">{ }</text>

          <text y="-10" 
                font-family="system-ui, -apple-system, sans-serif" 
                font-size="24" 
                font-weight="600" 
                fill="#ffffff" 
                filter="url(#neonGlow)">course_name_line_1</text>
          <text y="20" 
                font-family="system-ui, -apple-system, sans-serif" 
                font-size="24" 
                font-weight="600" 
                fill="#ffffff" 
                filter="url(#neonGlow)">course_name_line_2</text>
          <text y="50" 
                font-family="system-ui, -apple-system, sans-serif" 
                font-size="24" 
                font-weight="600" 
                fill="#ffffff" 
                filter="url(#neonGlow)">course_name_line_3</text>

          <text y="85" 
                font-family="monospace" 
                font-size="16" 
                fill="#00F260" 
                filter="url(#neonGlow)">achievement_status</text>

          <text y="105" 
                font-family="monospace" 
                font-size="14" 
                fill="url(#accentGradient)" 
                filter="url(#neonGlow)">PROGRAME.TECH</text>
        </g>
      </svg>`;

    // Process the course name into lines
    const cleanName = name?.trim() || 'Unknown Course';
    const words = cleanName.split(' ');
    const lines = [];
    let currentLine = [];
    
    for (const word of words) {
      if (currentLine.length === 2 || 
          (currentLine.join(' ').length + word.length > 20)) {
        lines.push(currentLine.join(' '));
        currentLine = [word];
      } else {
        currentLine.push(word);
      }
    }
    if (currentLine.length > 0) {
      lines.push(currentLine.join(' '));
    }

    while (lines.length < 3) lines.push('');

    // Set achievement status based on badge type
    const achievementStatus = type === BADGE_TYPES.COMPLETION ? 
      '✓ COMPLETED' : 
      type === BADGE_TYPES.EXCELLENCE ? 
      '★ EXCELLENCE' : 
      '→ IN PROGRESS';

    // Replace placeholders in SVG template
    let finalSvg = svgTemplate
      .replace('course_name_line_1', lines[0])
      .replace('course_name_line_2', lines[1])
      .replace('course_name_line_3', lines[2])
      .replace('achievement_status', achievementStatus);

    // Convert to base64
    const base64SVG = btoa(unescape(encodeURIComponent(finalSvg)));
    return `data:image/svg+xml;base64,${base64SVG}`;
  } catch (error) {
    console.error('Error generating badge image:', error);
    const fallbackSvg = `
      <svg width="400" height="400" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="#2D3436"/>
        <text x="200" y="200" text-anchor="middle" font-family="monospace" font-size="24" fill="#00F260">Badge</text>
      </svg>`;
    const base64Fallback = btoa(unescape(encodeURIComponent(fallbackSvg)));
    return `data:image/svg+xml;base64,${base64Fallback}`;
  }
};